import config from "../configLoader";

/**
 * Supervisor picker presentation, in one place.
 *
 * WCDP identifies supervisors by employee number (altEmployeeID, "EE#") rather than by
 * name, so their pickers lead with the number, order by it numerically ascending, and —
 * because FloatMenuButton and SearchablePicker both filter on the rendered text — become
 * searchable by number for free once the number is in the label.
 *
 * Everywhere else the flag is absent and every helper here is a no-op: labels and
 * ordering stay exactly as they were.
 */

export const supervisorNumbersOn = (): boolean =>
    config.showSupervisorEmployeeNumbers === true;

/** EE# as a sort key. Blank or non-numeric → NaN, which the comparator pushes last. */
export const eeSortKey = (alt?: string | null): number => {
    const t = (alt ?? "").trim();
    return /^\d+$/.test(t) ? Number(t) : NaN;
};

/**
 * Option label: `1234 — Smith, John` when the flag is on and the EE# is usable,
 * otherwise `name` untouched. A supervisor with no EE# keeps a bare name rather than
 * an orphan separator, and sorts to the end of the list.
 */
export const supervisorOptionLabel = (name: string, alt?: string | null): string =>
    supervisorNumbersOn() && (alt ?? "").trim() ? `${(alt ?? "").trim()} — ${name}` : name;

export interface SupervisorSortable {
    name: string;
    altEmployeeID?: string | null;
}

/**
 * Returns a NEW array, ordered by EE# ascending when the flag is on (blank/non-numeric
 * EE# last, name-ordered among themselves).
 *
 * When the flag is off this applies `fallback` if given and otherwise preserves the
 * input order — that is what keeps every non-WCDP tenant's list byte-for-byte unchanged,
 * including the call sites that deliberately render the API's own ordering.
 */
export function sortSupervisorOptions<T extends SupervisorSortable>(
    items: T[],
    fallback?: (a: T, b: T) => number
): T[] {
    if (!supervisorNumbersOn()) return fallback ? [...items].sort(fallback) : [...items];
    return [...items].sort((a, b) => {
        const ka = eeSortKey(a.altEmployeeID);
        const kb = eeSortKey(b.altEmployeeID);
        const aNum = !Number.isNaN(ka);
        const bNum = !Number.isNaN(kb);
        if (aNum && bNum) return ka - kb || a.name.localeCompare(b.name);
        if (aNum !== bNum) return aNum ? -1 : 1;
        return a.name.localeCompare(b.name);
    });
}
